import { useState, useEffect, useRef } from "react";

const useTrackCommentForm = (initialFormValues, callback, validate) => {
  const [inputs, setInputs] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkEditorOpen, setLinkEditorOpen] = useState(false);
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [youtubeEditorOpen, setYoutubeEditorOpen] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [selectionStart, setSelectionStart] = useState(-1);
  const [selectionEnd, setSelectionEnd] = useState(-1);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmitting) {
      callback();
    }
  }, [errors]);

  const handleInputChange = (event) => {
    event.persist();
    setInputs((inputs) => ({ ...inputs, [event.target.name]: event.target.value }));
  };

  const getTextareaElement = () => {
    // Try multiple methods to get the textarea element
    // First try by ID (most reliable)
    const byId = document.getElementById("trackComment");
    if (byId && byId.tagName === "TEXTAREA") return byId;
    
    // Then try the ref
    if (textareaRef.current) {
      let textarea = textareaRef.current;
      // Handle react-bootstrap Form.Control ref - it may be wrapped
      if (textarea.querySelector) {
        textarea = textarea.querySelector("textarea") || textarea;
      }
      if (textarea && textarea.tagName === "TEXTAREA") return textarea;
    }
    
    return null;
  };

  const getSelectedText = () => {
    const textarea = getTextareaElement();
    if (!textarea || typeof textarea.selectionStart === "undefined") return "";
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    if (start === end) return ""; // No selection
    return textarea.value.substring(start, end);
  };

  const captureSelectedText = () => {
    // Capture selection immediately before focus is lost
    const textarea = getTextareaElement();
    if (!textarea) {
      setSelectedText("");
      setSelectionStart(-1);
      setSelectionEnd(-1);
      return "";
    }
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = start !== end ? textarea.value.substring(start, end) : "";
    
    // Debug log
    console.log("Captured selection:", { selected, start, end, textareaValue: textarea.value });
    
    setSelectedText(selected);
    setSelectionStart(start);
    setSelectionEnd(end);
    return selected;
  };

  const getCursorPosition = () => {
    const textarea = getTextareaElement();
    if (!textarea) return 0;
    return textarea.selectionStart;
  };

  const setCursorPosition = (position) => {
    const textarea = getTextareaElement();
    if (!textarea) return;
    textarea.focus();
    textarea.setSelectionRange(position, position);
  };

  const replaceSelectedText = (newText) => {
    const textarea = getTextareaElement();
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = inputs.trackComment || inputs.message || "";
    const before = currentValue.substring(0, start);
    const after = currentValue.substring(end);
    const newValue = before + newText + after;
    
    const fieldName = inputs.trackComment !== undefined ? "trackComment" : "message";
    setInputs((inputs) => ({ ...inputs, [fieldName]: newValue }));
    
    // Set cursor position after inserted text
    setTimeout(() => {
      const updatedTextarea = getTextareaElement();
      if (updatedTextarea) {
        updatedTextarea.focus();
        updatedTextarea.setSelectionRange(start + newText.length, start + newText.length);
      }
    }, 0);
  };

  const insertAtCursor = (text) => {
    const textarea = getTextareaElement();
    if (!textarea) return;
    
    const cursorPos = textarea.selectionStart;
    const currentValue = inputs.trackComment || inputs.message || "";
    const before = currentValue.substring(0, cursorPos);
    const after = currentValue.substring(cursorPos);
    const newValue = before + text + after;
    
    const fieldName = inputs.trackComment !== undefined ? "trackComment" : "message";
    setInputs((inputs) => ({ ...inputs, [fieldName]: newValue }));
    
    // Set cursor position after inserted text
    setTimeout(() => {
      const updatedTextarea = getTextareaElement();
      if (updatedTextarea) {
        updatedTextarea.focus();
        updatedTextarea.setSelectionRange(cursorPos + text.length, cursorPos + text.length);
      }
    }, 0);
  };

  const insertLinkTag = (linkData) => {
    const { url, linkText } = linkData;
    
    // If we have stored selection positions, use them
    if (selectedText && selectedText.trim() !== "" && selectionStart >= 0 && selectionEnd >= 0) {
      const currentValue = inputs.trackComment || inputs.message || "";
      const before = currentValue.substring(0, selectionStart);
      const after = currentValue.substring(selectionEnd);
      const linkHtml = `<a href="${url}" target="_blank">${linkText || selectedText}</a>`;
      const newValue = before + linkHtml + after;
      
      const fieldName = inputs.trackComment !== undefined ? "trackComment" : "message";
      setInputs((inputs) => ({ ...inputs, [fieldName]: newValue }));
      
      // Set cursor position after inserted text
      setTimeout(() => {
        const textarea = getTextareaElement();
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(selectionStart + linkHtml.length, selectionStart + linkHtml.length);
        }
      }, 0);
    } else {
      // No selection, insert link at cursor position
      const linkHtml = `<a href="${url}" target="_blank">${linkText || url}</a>`;
      insertAtCursor(linkHtml);
    }
    
    setLinkEditorOpen(false);
    setSelectedText("");
    setSelectionStart(-1);
    setSelectionEnd(-1);
  };

  const insertImageTag = (imageData) => {
    const { url } = imageData;
    const imageHtml = `<img src="${url}" class="img-responsive img-inserted" border="0" />`;
    
    // Insert at cursor position with space before if needed
    const currentValue = inputs.trackComment || inputs.message || "";
    const cursorPos = getCursorPosition();
    const beforeCursor = currentValue.substring(0, cursorPos);
    const needsSpace = beforeCursor.trim() !== "" && !beforeCursor.endsWith(" ") && !beforeCursor.endsWith("\n");
    const textToInsert = needsSpace ? " " + imageHtml : imageHtml;
    
    insertAtCursor(textToInsert);
    setImageEditorOpen(false);
  };

  const insertYouTubeTag = (youtubeData) => {
    const { videoId } = youtubeData;
    const youtubeHtml = `<iframe class="youtube-embed" width="100%" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;

    const currentValue = inputs.trackComment || inputs.message || "";
    const cursorPos = getCursorPosition();
    const beforeCursor = currentValue.substring(0, cursorPos);
    const needsSpace = beforeCursor.trim() !== "" && !beforeCursor.endsWith(" ") && !beforeCursor.endsWith("\n");
    const textToInsert = needsSpace ? " " + youtubeHtml : youtubeHtml;

    insertAtCursor(textToInsert);
    setYoutubeEditorOpen(false);
  };

  // Legacy functions for backward compatibility
  const insertLinkTagLegacy = () => {
    const selected = getSelectedText();
    const link = selected.startsWith('http') 
      ? `<a href="${selected.trim()}" target="_blank">${selected}</a>` 
      : `<a href="PASTE_URL_HERE" target="_blank">${selected}</a>`;
    
    if (selected) {
      replaceSelectedText(link);
    } else {
      insertAtCursor(link);
    }
  };

  const imageTag = `<img src="ADD_YOUR_URL_HERE" class="img-responsive" border="0" />`;
  const insertImageTagLegacy = () => {
    const currentValue = inputs.trackComment || inputs.message || "";
    const newValue = currentValue ? currentValue + " " + imageTag : imageTag;
    const fieldName = inputs.trackComment !== undefined ? "trackComment" : "message";
    setInputs((inputs) => ({ ...inputs, [fieldName]: newValue }));
  };

  const resizeImage = (imageSrc, widthPercent) => {
    const currentValue = inputs.trackComment || inputs.message || "";
    const fieldName = inputs.trackComment !== undefined ? "trackComment" : "message";
    
    // Escape special regex characters in the src
    const escapedSrc = imageSrc.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Find the image tag with this src and update its class
    const imageRegex = new RegExp(`(<img[^>]*src=["']${escapedSrc}["'][^>]*>)`, 'gi');
    
    const updatedValue = currentValue.replace(imageRegex, (match) => {
      // Remove existing width classes
      let updatedMatch = match.replace(/\s+img-inserted-(25|50|75|125)/g, '');
      
      // Add new width class
      const widthClass = `img-inserted-${widthPercent}`;
      
      // Check if class attribute exists
      if (updatedMatch.includes('class=')) {
        // Add to existing class attribute
        updatedMatch = updatedMatch.replace(/class=["']([^"']*)["']/i, (fullMatch, classes) => {
          return `class="${classes} ${widthClass}"`;
        });
      } else {
        // Add new class attribute
        updatedMatch = updatedMatch.replace(/>$/, ` class="${widthClass}">`);
      }
      
      return updatedMatch;
    });
    
    setInputs((inputs) => ({ ...inputs, [fieldName]: updatedValue }));
  };

  const clearForm = () => {
    setInputs(initialFormValues);
    setLinkEditorOpen(false);
    setImageEditorOpen(false);
    setYoutubeEditorOpen(false);
    setSelectedText("");
    setSelectionStart(-1);
    setSelectionEnd(-1);
  };

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    setErrors(validate(inputs));
    setIsSubmitting(true);
  };

  return {
    handleInputChange,
    handleSubmit,
    insertLinkTag,
    insertImageTag,
    insertYouTubeTag,
    insertLinkTagLegacy,
    insertImageTagLegacy,
    resizeImage,
    inputs,
    clearForm,
    errors,
    textareaRef,
    getSelectedText,
    captureSelectedText,
    selectedText,
    linkEditorOpen,
    setLinkEditorOpen,
    imageEditorOpen,
    setImageEditorOpen,
    youtubeEditorOpen,
    setYoutubeEditorOpen,
  };
};

export default useTrackCommentForm;
